/*
Description: This is the file that will handle interacting with the Fusion EMR (app.fusionwebclinic.com)
Date: 4/4/2024
Author: Jacob Michalski

Notes:
    All Injectors need the following functions:
        getPageInfo
        getPatientInfo
        getQuestions
        answerQuestions (Consider combining these and making the single a private function)
        answerQuestion
*/

// Function to navigate through iframes and get the final document
function getFinalDocument() {
  if (document.getElementsByTagName('iframe').length === 0) {
    return document;
  }
  const frame2 = document.getElementsByTagName('iframe')[1]; // Assuming Frame 2 is the second iframe in the document
  const frame2Document =
    frame2.contentDocument || frame2.contentWindow.document;
  const framesetElement =
    frame2Document.documentElement.querySelector('frameset');

  if (framesetElement) {
    const frames = framesetElement.querySelectorAll('frame');
    const frame3 = frames[2];
    const frame3Document =
      frame3.contentDocument || frame3.contentWindow.document;
    const frameContentWrapper = frame3Document.getElementById(
      'frameContentWrapper'
    );

    if (frameContentWrapper) {
      const frMainWrapper = frameContentWrapper.querySelector('#frMainWrapper');

      if (frMainWrapper) {
        const iframe = frMainWrapper.querySelector('iframe.frmain');

        if (iframe) {
          return iframe.contentDocument || iframe.contentWindow.document;
        }
      }
    }
  }
  console.error("Error: Couldn't access final document.");
  return null;
}

export function getPageInfo() {
  // function getPageInfo() {
  /*
      Description: This function will get the page info from the note
      Return: A dictionary with the page info
          {
              "URL": Base URL for the page,
              "Note Type": "PT Daily/SOAP Note",...
          }
      */
  const finalDocument = getFinalDocument();
  if (!finalDocument) return null;
  const baseURL = window.location.origin;
  const encounterStamp = finalDocument.querySelector(
    '.encounter-stamp.autostart.encounter-briefing'
  );
  if (!encounterStamp) {
    console.error("Error: Couldn't find encounter stamp.");
    return null;
  }
  let noteType = '';
  if (encounterStamp) {
    const title = encounterStamp.getAttribute('title');
    if (title) {
      const parts = title.split('|');
      if (parts.length > 1) {
        noteType = parts[1].trim();
      } else {
        console.error('Error: Unable to parse title into parts.');
      }
    } else {
      console.error('Error: Unable to retrieve title attribute.');
    }
  } else {
    console.error('Error: encounterStamp element not found.');
  }
  return {
    URL: baseURL,
    'Note Type': noteType || 'Athena Note'
  };
}

export function getPatientInfo() {
  // function getPatientInfo() {
  /*
      Description: This function will get the patient info from the note
      Return: A dictionary with the patient info
      {
          "EMR ID": 0x1234 or "michalskij"
          "First Name":firstName,
          "Last Name": lastName,
          "DOB": dob,
      }
      */
  // Assuming the element's text is "Halper John, he/him (EMR ID: 42332797)"
  // Select the HTML element containing the name information
  // Get the HTML element
  // Select the element with class 'autostart' within the DOM structure you described
  //var myDivContent = document.querySelector("#myDiv").innerHTML;
  // Get Frame 2
  const finalDocument = getFinalDocument();
  if (!finalDocument) return null;
  const nimbusBanner = finalDocument.querySelector(
    '#nimbus-banner-container .autostart'
  );
  if (!nimbusBanner) {
    console.error("Error: Couldn't find patient info.");
    return null;
  }
  const patientData = JSON.parse(nimbusBanner.getAttribute('data-props'));
  return {
    'EMR ID': patientData.patientId,
    'First Name': patientData.firstName,
    'Last Name': patientData.lastName,
    DOB: patientData.birthDate
  };
}

/* 
questions - This is where we will store the questions
          - a list of dictionaries
     [{
        "Question": "Where was the injury?", 
        "Type": "Multiple Choice", 
        "Choices": ["Back", "Neck", "Shoulder"]
     },
        ...
        ]
 
*/
var questions = [];
var questionInteractions = {};

/*
question Interactions - This is where we will store the interactions for each question
                        - a dictionary of dictionaries
         {
            "Where was the injury?": {
                "Type": "Multiple Choice",
                "Choices"{
                    "Back": () => {click the back button},
                    "Neck": () => {click the neck button},
                    "Shoulder": () => {click the shoulder button}
                }
            },
        },
        {
            "What is the injury?": {
                "Type": "Short Answer",
                "Input": (val) => {input the value into the input box}
        }
            ...
         }
*/

export async function getQuestions() {
  const finalDocument = getFinalDocument();
  if (!finalDocument) return [];

  questions = [];
  questionInteractions = {};

  extractCardStackElements(finalDocument);
  extractAdditionalQuestions(finalDocument);

  return generateFinalQuestionList();
}

function extractCardStackElements(finalDocument) {
  const parentElement = finalDocument.querySelector(
    '.exam-container-small-wrapper .card-container .card-scrolling-container'
  );
  if (!parentElement) {
    console.error('Parent element not found.');
    return;
  }

  const cardStackElements = parentElement.querySelectorAll('.card-stack');
  cardStackElements.forEach((cardStack) => {
    try {
      const cardTitleElements = cardStack.querySelectorAll(
        '.card.c_card.c__card--title'
      );
      cardTitleElements.forEach((cardTitle) => {
        extractQuestionFromCardTitle(finalDocument, cardTitle);
      });
    } catch (e) {
      // These seem like expected errors, so we'll just ignore them...
      // console.error('Error in extracting card stack elements:', e);
      // getLogFromInjector();
    }
  });
}

function extractQuestionFromCardTitle(finalDocument, cardTitle) {
  const questionElement = cardTitle.querySelector(
    '.athena-header.header-with-rule.card-header.scrolling-header.exam-header.intake-header.sticky-header .text'
  );
  if (!questionElement) return;

  const questionText = questionElement.textContent.trim();
  if (questionText === '') return;

  const input = cardTitle.querySelector(
    '.rich-text-container.autostart .rich-text-input'
  );
  const existingNote = getExistingNote(
    cardTitle.querySelector('.rich-text-input').textContent
  );

  questions.push({
    Key: questionText,
    Question: questionText,
    Type: 'Long Answer',
    ExistingNote: existingNote || ''
  });

  questionInteractions[questionText] = {
    Type: 'Long Answer',
    ExistingNote: existingNote,
    Input: createInputFunction(finalDocument, input, existingNote)
  };
}

function isEmptyOrNull(aiResponse) {
  return (
    aiResponse === null ||
    aiResponse === undefined ||
    aiResponse === '' ||
    aiResponse === 'None'
  );
}

function createInputFunction(finalDocument, input, existingNote) {
  return async (val, replyType) => {
    if (isEmptyOrNull(val)) {
      return;
    }
    let newNote;
    if (replyType === 'append' && existingNote) {
      newNote = existingNote + '\n' + val;
    } else {
      newNote = val;
    }
    // if the class is a slate-editor, use the slate editor else normal input
    if (
      [...input.classList].some((className) =>
        className.includes('slate-editor')
      )
    ) {
      // Slate appends in-editor from its live text; existingNote is a stale, flattened snapshot
      return slateEditorGivenSelectorInsertText(
        getSelector(input),
        val,
        replyType === 'append'
      );
    } else {
      return normalInput(input, newNote);
    }
  };
}

function normalInput(input, val) {
  input.textContent = val;
  return input.dispatchEvent(
    new Event('rich-text-prepare-for-save', {
      bubbles: true,
      cancelable: true
    })
  );
}

function extractAdditionalQuestions(finalDocument) {
  // List of selectors to iterate over
  const selectors = [
    'div.uta_c_editor__slate-editor-wrapper > div',
    'div.rich-text-input'
  ];

  // Iterate over the list of selectors
  selectors.forEach((selector) => {
    // Select all elements matching the current selector
    const elements = finalDocument.querySelectorAll(selector);

    // Iterate over the elements and extract the question text and existing note
    elements.forEach((element) => {
      try {
        let questionText = getEditorLabel(element);
        // Check if the question text already exists in the questions array and if so we don't need to add it again
        // Upon testing, I was able to fill in the same box from either of them so just keep the first. Shrug
        if (questions.some((question) => question.Question === questionText)) {
          return;
          // questionText += ` (${questions.filter(q => q.Question === questionText).length + 1})`;
        }
        const existingNote = element.textContent.trim();

        // Add the question to the questions array
        questions.push({
          Key: questionText,
          Question: questionText,
          Type: 'Long Answer',
          ExistingNote: existingNote || ''
        });

        // Add the question interaction to the questionInteractions object
        const elementSelector = getSelector(element);
        questionInteractions[questionText] = {
          Type: 'Long Answer',
          ExistingNote: existingNote,
          Input: async (val, replyType) => {
            return answerShortAnswer(
              finalDocument,
              elementSelector,
              val,
              replyType,
              selector.includes('slate')
            );
          }
        };
      } catch (error) {
        console.log('Error in extracting question from', element, error);
      }
    });
  });
}

function getEditorLabel(element) {
  const labelElement = findParentMatch(element, '.text');
  return labelElement.innerText.trim();
}

function findParentMatch(element, selector) {
  while (element) {
    if (element.querySelector(selector)) {
      return element.querySelector(selector);
    }
    element = element.parentElement;
  }
  return null; // Return null if no matching parent is found
}

async function answerShortAnswer(
  finalDocument,
  localSubSectionSelector,
  val,
  replyType,
  forceSlateEditor = false
) {
  // console.log('Local Sub Section Selector: ' + localSubSectionSelector);
  // console.log('Value: ' + val);
  // console.log('Reply Type: ' + replyType);

  // If it's a slate editor, use the slate editor function
  if (localSubSectionSelector.includes('slate-editor') || forceSlateEditor) {
    if (isEmptyOrNull(val)) {
      return;
    }
    return slateEditorGivenSelectorInsertText(
      localSubSectionSelector,
      val,
      replyType === 'append'
    );
  }

  // Not the slate editor, use the normal input
  try {
    const localSubQuestionTextArea = finalDocument.querySelector(
      localSubSectionSelector
    );
    // console.log('Local Sub Question Text Area: ' + localSubQuestionTextArea);
    localSubQuestionTextArea.click();
    await sleep(10);
    if (isEmptyOrNull(val)) {
      return;
    }
    // Actually Type
    if (replyType === 'append') {
      setReactTextareaValue(
        finalDocument,
        localSubQuestionTextArea,
        localSubQuestionTextArea.value + '\n' + val
      );
    } else {
      setReactTextareaValue(finalDocument, localSubQuestionTextArea, val);
    }
  } catch (error) {
    getLogFromInjector();
  }
}

async function setReactTextareaValue(finalDocument, spanElement, value) {
  // console.log('Setting React Textarea Value:', spanElement, value);

  // Set the inner HTML of the span element
  spanElement.innerHTML = value;

  // Create and dispatch an input event to simulate typing
  const inputEvent = new Event('input', { bubbles: true, cancelable: true });
  spanElement.dispatchEvent(inputEvent);
  // console.log('Input Event:', inputEvent);

  // Simulate focus and blur events to trigger API call for saving
  spanElement.focus();
  await sleep(50);
  // console.log('Element:', spanElement);
  const blurEvent = new Event('blur', { bubbles: true, cancelable: true });
  spanElement.dispatchEvent(blurEvent);
  // console.log('Blur Event:', blurEvent);
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function generateFinalQuestionList() {
  const finalQuestionList = [];
  Object.keys(questionInteractions).forEach((key) => {
    const interaction = {
      Key: key,
      Question: key,
      Type: questionInteractions[key].Type,
      ExistingNote: questionInteractions[key].ExistingNote,
      Input: questionInteractions[key].Input
    };
    finalQuestionList.push(interaction);
  });
  return finalQuestionList;
}

function getExistingNote(value) {
  if (typeof value !== 'string') {
    return null;
  }

  let val = value?.trim();
  return val === '' ? null : val;
}

export function answerQuestions(answers) {
  // function answerQuestions(answers) {
  /*
      Description: This function will take the answers and populate the questions
      
      Args: 
          answers: A dictionary of answers
              "Question": "The question in question"
              "Answer": "The answer to the question"
     
      Return: A list of any questions that were not answered, perhaps the answer didn't match the questions expected or whatnot...
          [
              {
                  Question: "Where was the injury?", 
                  Answer: "Back" 
                  Error: "The error message" (Choice not found, etc)
              },
              ...
          }
  
      */
  var errors = [];
  answers.forEach((answer) => {
    var error = answerQuestion(answer['Question'], answer['Answer']);
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });
  console.error('Errors:');
  console.error(errors);
  return errors;
}

var isLocked = false;
export async function answerQuestion(
  question,
  answer,
  replyType,
  totalWaitTime = 0
) {
  // function answerQuestion(question, answer) {
  /*
    Description: This function will take the answer and populate the question
    
    Args: 
        question: The question in question
        answer: The answer to the question
   
    Return: null if success otherwise a string with the error message
    */

  // console.log("Answering Question: '" + question + "' with '" + answer+"'")

  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.log(Object.keys(questionInteractions));
    console.log(questionInteractions);
    return 'Question not found';
  }

  // if we are locked, delay for a random amount of time between .1 and 1 seconds waiting no longer than 3 seconds
  console.log('Total Wait Time: ' + totalWaitTime);
  console.log('Is Locked: ' + isLocked);
  if (isLocked && totalWaitTime < 3000) {
    let delay = Math.random() * 1000;
    return sleep(delay).then(() => {
      return answerQuestion(question, answer, replyType, totalWaitTime + delay);
    });
  }

  // Set our lock
  isLocked = true;

  try {
    // If it is a short answer or long answer, just populate the input
    if (questionInteractions[question]['Input']) {
      // console.log(questionInteractions[question]['Input']);
      const result = await questionInteractions[question]['Input'](
        answer,
        replyType
      );
      if (result === 'failure') {
        return 'Failed to fill the Athena editor';
      }
    }
  } finally {
    isLocked = false;
  }
  return true;
}

// Need infinite loop protection
export async function getLogFromInjector(depth = 0) {
  return;
  if (depth > 3) {
    return;
  }
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector(depth + 1);
  }
}

/* SLATE EDITOR */
// We had to inject some code into the main land to get access to the slate editor
// Now we just call those messages
function slateEditorGivenSelectorInsertText(selector, text, append = false) {
  return callToMainLand('slateEditorGivenSelectorInsertText', {
    selector: selector,
    text: text,
    append: append
  }).catch(() => 'failure');
}

async function callToMainLand(action, data) {
  let payload = { action: action };
  for (const key in data) {
    payload[key] = data[key];
  }
  return new Promise((resolve, reject) => {
    // Handle the response coming back
    const handleMessage = (event) => {
      if (event.data.action === action + '_response') {
        resolve(event.data.result);
        window.removeEventListener('message', handleMessage);
      }
    };
    window.addEventListener('message', handleMessage);

    // Post the message to the main land
    window.postMessage(payload, '*');

    // The main world now replies after the insert + its 100ms pre-blur wait, so allow more than that
    setTimeout(() => {
      window.removeEventListener('message', handleMessage);
      reject(new Error('Timeout occurred while waiting for response message'));
    }, 3000);
  });
}

function getSelector(elm) {
  // This function will return the selector for a given element
  if (elm.tagName === 'BODY') return 'BODY';
  const names = [];
  while (elm.parentElement && elm.tagName !== 'BODY') {
    if (elm.id) {
      names.unshift('#' + elm.getAttribute('id')); // getAttribute, because `elm.id` could also return a child element with name "id"
      break; // Because ID should be unique, no more is needed. Remove the break, if you always want a full path.
    } else {
      let c = 1,
        e = elm;
      for (; e.previousElementSibling; e = e.previousElementSibling, c++);
      names.unshift(elm.tagName + ':nth-child(' + c + ')');
    }
    elm = elm.parentElement;
  }
  return names.join('>');
}
